import cx from 'classnames';
import * as _ from 'lodash';
import { useRef } from 'react';
import { Transition } from 'react-transition-group';
import type { FunctionComponent, ReactNode, ReactNodeArray, RefObject } from 'react';

import { useOnClickOutside, useOnEscPress } from '../../../hooks';

import styles from './Drawer.module.less';

type Props = {
  /**
   * If the Drawer is open or not
   */
  isOpen: boolean;
  /**
   * Function to close the drawer
   */
  onClose: () => void;
  /**
   * How long the slide animation should last in ms (@default 500)
   */
  slideDuration?: number;
  /**
   * A ref for the element which displayed the Drawer. Usually you want this to be excluded from the useClickOutside handler
   */
  clickTarget?: RefObject<HTMLElement>;
  /**
   * Allows you to temporarily bail out of the click outside behaviour,
   * for example if you have a modal open over top of the drawer.
   */
  shouldSkipClickOutside?: boolean;
  /**
   * "Slot" prop which allows consumers to inject additional buttons to the left of the close button.
   */
  buttonsSlot?: ReactNode | ReactNodeArray;
  /**
   * Drawer contents
   */
  children: ReactNode;
};

/**
 * Drawer is an overlay-style component which will slide out from the right-hand
 * side of the page and may contain any arbitrary content.
 */
const Drawer: FunctionComponent<Props> = ({
  children,
  isOpen,
  onClose,
  slideDuration = 500,
  clickTarget,
  shouldSkipClickOutside,
  buttonsSlot,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  //  When the modal is closed, clicking "outside" shouldn't call the onClose handler.
  const handleClose = isOpen ? onClose : _.noop;

  //  Ensure that the drawer is closed if clicked outside of it.
  //  Optionally include the clickTarget which triggered the opening of the Drawer, to ensure it doesn't close.
  useOnClickOutside({
    ref,
    handler: handleClose,
    ignoredElements: clickTarget ? [clickTarget] : undefined,
    shouldSkipClickOutside,
  });
  //  Ensure the drawer is closed when the user hits the Esc key.
  useOnEscPress(handleClose);

  return (
    <Transition nodeRef={ref} in={isOpen} timeout={slideDuration}>
      {state => (
        <div
          ref={ref}
          className={cx(styles.drawer, styles[state])}
          style={{ transitionDuration: `${slideDuration}ms` }}
          aria-hidden={!isOpen}
          // Id is for resetting the scroll position in licensing issue drawer.
          id="drawer"
        >
          <div className={styles.buttonGroup}>
            {buttonsSlot}
            <button type="button" className={styles.closeButton} onClick={onClose}>
              <span aria-hidden="true">×</span>
              <span className="sr-only">Close</span>
            </button>
          </div>
          {children}
        </div>
      )}
    </Transition>
  );
};

export default Drawer;
